import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  DataTable,
  LoadingState,
  PageHeader,
} from '../../../group_leader/components/common'
import PlayerRow from '../common/PlayerRow'
import { makeT, sharedT } from '../../../group_leader/helpers/i18n'
import { buildSlots, formatHandicap, playerFullName, playerInitials } from '../../../group_leader/helpers/player'
import { isPresent } from '../../../group_leader/helpers/value'
import {
  glApi,
  useListRoundsQuery,
  useListLeagueCoursesQuery,
  useGetTeeSheetQuery,
  useGetUnplacedPlayersQuery,
  useGetLeagueCustomFieldsQuery,
  useListTournamentSpecsQuery,
  useCreateRowMutation,
  useAddPlayersMutation,
  useRemovePlayersMutation,
  useSwapPlayersMutation,
  useAddBlindDrawMutation,
  useRemoveBlindDrawMutation,
  useReorganizeMutation,
  useReorganizePlayersMutation,
  useDeleteFoursomeMutation,
  useSetTeeTimeMutation,
  useSetTeeTimesWithIntervalMutation,
  useSetShotgunMutation,
  useSetHoleMutation,
  useUpdateRoundExpressEventSettingsMutation,
  useSetCourseAndTeeMutation,
} from '../../../group_leader/store/glApi'
import { onConfirm, showErrorNotification } from '../../../shared/helpers'
import LiveMessage from '../common/LiveMessage'
import WizardFooter from '../common/WizardFooter'
import PairingsBench from './pairings/PairingsBench'
import PairingsMobileCard from './pairings/PairingsMobileCard'
import PairingsOverlays from './pairings/PairingsOverlays'
import ReorganizeModal from './pairings/ReorganizeModal'
import HoleCell from './pairings/HoleCell'
import TeeTimeCell from './pairings/TeeTimeCell'
import PairingsConfigPanel from './pairings/PairingsConfigPanel'
import { usePairingsDnD } from './pairings/usePairingsDnD'
import useJobPoll from '../../hooks/useJobPoll'
import {
  MAX_RUN_INTERVAL_MINUTES,
  WALKON_RESTORE_GROUPS,
  courseHoleCount,
  fieldCourseId,
  fieldTeeId,
  freeHolesInOrder,
  hasTeamGame,
  holesTypeFor,
  shiftTime24,
  REORGANIZE_OUTCOME,
  startHoleNumber,
  runInterval,
  shotgunReversed,
  teeWritesForField,
  to12hr,
  to24hr,
} from '../../helpers/pairings'
import { playsWomensTee, sheetGenderTees } from '../../helpers/players'
import { goToNextStep, goToPreviousStep, markStepCompleted, setSetupConfig } from '../../store/wizardSlice'

const DEFAULT_TEE_TIME = '09:00'

// The panel's tee time, or the default when blank, so a run never starts at an empty string.
const runStartTime = (time24) => ((/^\d{1,2}:\d{2}$/).test(time24 || '') ? time24 : DEFAULT_TEE_TIME)

const t = makeT('group_leader.event_setup.pairings')

// The list a Map keeps under `key`, made by `make` the first time.
const entryFor = (map, key, make) => {
  if (!map.has(key)) { map.set(key, make()) }
  return map.get(key)
}

const seatedIds = (groups) => groups
  .flatMap((g) => (g.attributes?.players || []).map((p) => String(p.player_round_id || p.id)))

const PairingsStep = () => {
  const dispatch = useDispatch()
  const leagueId = useSelector((s) => s.misc.data.leagueId)
  const roundId = useSelector((s) => s.wizard.roundId)
  const setupConfig = useSelector((s) => s.wizard.setupConfig)

  // A team game builds its teams from the tee sheet, so walk-on cannot host one.
  const { data: specsData } = useListTournamentSpecsQuery(
    { leagueId, roundId },
    { skip: !leagueId || !roundId },
  )
  const teamGamePresent = hasTeamGame(specsData?.data)

  // One popover at a time, so one piece of state describes it:
  //   { kind: 'slotPicker' | 'slotMenu' | 'blindPicker' | 'swapPicker', rect, … }
  const [ overlay, setOverlay ] = useState(null)
  const [ liveMessage, setLiveMessage ] = useState('')
  // Toasted and announced: react-notifications carries no aria-live of its own.
  const notifyFailure = (message) => { showErrorNotification(message); setLiveMessage(message) }
  const closeOverlay = () => setOverlay(null)

  const [ isReorganizing, setIsReorganizing ] = useState(false)
  const [ reorganizeModalOpen, setReorganizeModalOpen ] = useState(false)
  // Which job is running, so the button label and status line stay put while the sheet fills.
  const [ placingField, setPlacingField ] = useState(false)
  const [ pendingGroupSize, setPendingGroupSize ] = useState(null)

  const [ ttType, setTtType ] = useState(() => setupConfig?.startType || 'teetimes')
  // The group size to restore when the leader leaves walk-on (which forces size 1).
  const preWalkonSizeRef = useRef('4')
  // Open in walk-on if the round already sits at size 1 (set in a prior session), once.
  const walkonSyncedRef = useRef(false)
  const [ ttTime, setTtTime ] = useState(() => setupConfig?.teeTime || DEFAULT_TEE_TIME)
  const [ ttInterval, setTtInterval ] = useState(() => setupConfig?.interval || '8')
  // The run's starting hole as typed; legacy 'hole1'/'hole10'/'split' become their hole number.
  const [ ttHole, setTtHole ] = useState(() => String(startHoleNumber(setupConfig?.startHole)))
  const [ ttShotgunTime, setTtShotgunTime ] = useState(() => setupConfig?.shotgunTime || DEFAULT_TEE_TIME)
  const [ ttHoleAssignment, setTtHoleAssignment ] = useState(() => (setupConfig?.reverseDir ? 'reverse' : 'sequential'))

  // Open while the sheet is empty, closed once golfers are placed; the leader's toggle wins after.
  const [ configOpen, setConfigOpen ] = useState(true)
  const configUserSetRef = useRef(false)

  const teeTimeSyncedRef = useRef(false)
  const dnd = usePairingsDnD()

  const { data: roundsData } = useListRoundsQuery(leagueId, { skip: !leagueId })
  const { data: coursesData } = useListLeagueCoursesQuery(leagueId, { skip: !leagueId })
  const { data: teeSheetData, isLoading: sheetLoading, refetch: refetchTeeSheet } = useGetTeeSheetQuery(
    { leagueId, roundId },
    { skip: !leagueId || !roundId },
  )
  const { data: unplacedData, refetch: refetchUnplaced } = useGetUnplacedPlayersQuery(
    { leagueId, roundId },
    { skip: !leagueId || !roundId },
  )
  const { data: customFieldsData } = useGetLeagueCustomFieldsQuery(
    { leagueId, roundId },
    { skip: !leagueId || !roundId },
  )

  const [ createRow, { isLoading: creatingRow } ] = useCreateRowMutation()
  const [ addPlayers, { isLoading: addingPlayer } ] = useAddPlayersMutation()
  const [ removePlayers, { isLoading: removing } ] = useRemovePlayersMutation()
  const [ swapPlayers ] = useSwapPlayersMutation()
  const [ addBlindDraw ] = useAddBlindDrawMutation()
  const [ removeBlindDraw ] = useRemoveBlindDrawMutation()
  const [ reorganize ] = useReorganizeMutation()
  const [ reorganizePlayers ] = useReorganizePlayersMutation()
  const pollJob = useJobPoll({ leagueId, roundId })
  const [ deleteFoursome, { isLoading: deletingRow } ] = useDeleteFoursomeMutation()
  const [ setOneTeeTime ] = useSetTeeTimeMutation()
  const [ setTeeTimesWithInterval, { isLoading: applyingTimes } ] = useSetTeeTimesWithIntervalMutation()
  const [ setShotgun ] = useSetShotgunMutation()
  const [ setHole ] = useSetHoleMutation()
  const [ updateRoundExpressEventSettings ] = useUpdateRoundExpressEventSettingsMutation()
  const [ setCourseAndTee ] = useSetCourseAndTeeMutation()

  // Bulk sequences write `silent: true` and refresh once here, under the TMS API's 20 GETs a minute.
  const refreshTeeSheet = () => dispatch(glApi.util.invalidateTags([ 'TeeSheet' ]))

  const foursomes = teeSheetData?.data || []
  const unplaced = unplacedData?.players || []
  const customFields = customFieldsData?.custom_fields || []
  const allPlayers = foursomes.flatMap((g) => g.attributes?.players || [])
  const playerCount = allPlayers.length

  // No course id on the round, so read it off the golfers' tees; the league's only course is the fallback.
  const leagueCourses = coursesData?.data || []
  const roundCourseId = allPlayers.find((p) => p?.tee?.course_id)?.tee?.course_id
  const eventCourse = (isPresent(roundCourseId)
    && leagueCourses.find((c) => String(c.id) === String(roundCourseId)))
    || (leagueCourses.length === 1 ? leagueCourses[0] : null)
    || null
  const courseName = eventCourse?.attributes?.name || ''
  const holeCount = courseHoleCount(eventCourse)
  // Walk-on lists the enrolled golfers, deduped by id: mid-toggle both queries can hold one golfer.
  const seenWalkon = new Set()
  const walkonPlayers = [ ...allPlayers, ...unplaced ].filter((p) => {
    const id = p.player_round_id || p.id
    return !seenWalkon.has(id) && seenWalkon.add(id)
  })

  const serverRound = roundsData?.data?.find((r) => String(r.id) === String(roundId))
  const serverGroupSize = serverRound?.attributes?.pairing_group_size
    ? String(serverRound.attributes.pairing_group_size)
    : null
  const groupSize = pendingGroupSize ?? serverGroupSize ?? '4'
  const maxSize = parseInt(groupSize, 10)
  // A row can hold more than the configured size, so render as many seats as the widest row uses.
  const seatsUsedBy = (group) => [
    ...(group.attributes?.players || []),
    ...(group.attributes?.blinds || []),
  ].reduce((widest, occupant) => Math.max(widest, (occupant.position ?? 0) + 1), 0)
  const slotCount = foursomes.reduce((widest, group) => Math.max(widest, seatsUsedBy(group)), maxSize)
  // Only whether a shrink would displace someone -- the redeal itself is applyGroupSizeChange's job.
  const wouldDisplaceOccupants = (newSize) => foursomes.some((group) => seatsUsedBy(group) > newSize)
  // Walk-on is group size 1 (what scorecards read), including a server round already at 1.
  const isWalkon = ttType === 'walkon' || groupSize === '1'

  // Golfers on the sheet, or the walk-on roster; auto-collapse once placed, until the leader toggles.
  const placedCount = isWalkon ? walkonPlayers.length : playerCount
  useEffect(() => {
    if (configUserSetRef.current || sheetLoading) { return }
    setConfigOpen(placedCount === 0)
  }, [ placedCount, sheetLoading ])
  const toggleConfig = () => { configUserSetRef.current = true; setConfigOpen((open) => !open) }

  // From the sheet, not Redux, which does not survive a reload; Golfers is reported here too.
  useEffect(() => {
    if (placedCount + unplaced.length > 0) { dispatch(markStepCompleted('players')) }
    if (placedCount > 0) { dispatch(markStepCompleted('pairings')) }
  }, [ placedCount, unplaced.length ])

  // One-line recap shown on the collapsed header so the leader can read the current setup at a glance.
  const configSummary = (() => {
    if (isWalkon) { return t('config.summary_walkon') }
    const parts = [ t('config.summary_groups', { n: groupSize }) ]
    if (ttType === 'shotgun') {
      parts.push(t('config.summary_shotgun', { time: to12hr(ttShotgunTime) }))
    } else {
      parts.push(t('config.summary_teetimes', { time: to12hr(ttTime), interval: ttInterval }))
    }
    return parts.join(' · ')
  })()

  // Reads the sheet's tee times back into the form once, so the controls show the event's values.
  useEffect(() => {
    if (teeTimeSyncedRef.current || !teeSheetData?.data?.length) { return }
    const withTimes = teeSheetData.data.filter((f) => f.attributes?.tee_time)
    if (!withTimes.length) { return }
    teeTimeSyncedRef.current = true

    const firstTime24 = to24hr(withTimes[0].attributes.tee_time)
    if (!firstTime24) { return }

    const allSameTime = withTimes.every((f) => f.attributes.tee_time === withTimes[0].attributes.tee_time)
    if (allSameTime && withTimes.length > 1) {
      setTtShotgunTime(firstTime24)
      // Safe now that the shotgun branch only touches untimed rows.
      setTtType('shotgun')
      // Direction is stored nowhere, so it is inferred from the hole order.
      const reversed = shotgunReversed(withTimes.map((f) => Number(f.attributes?.hole)), holeCount)
      if (reversed !== null) { setTtHoleAssignment(reversed ? 'reverse' : 'sequential') }
    } else {
      setTtTime(firstTime24)
      // Only when the whole sheet agrees on one interval (see runInterval).
      const interval = runInterval(withTimes.map((f) => to24hr(f.attributes.tee_time)).filter(Boolean))
      if (interval) { setTtInterval(String(interval)) }
    }

    const firstHole = Number(withTimes[0].attributes?.hole || withTimes[0].attributes?.tee_number)
    if (Number.isInteger(firstHole) && firstHole > 0) { setTtHole(String(firstHole)) }
    // Keyed to the tee sheet only: a one-shot sync that must not fight the leader's edits.
  }, [ teeSheetData ])

  // A round already at group size 1 opens in walk-on, once.
  useEffect(() => {
    if (walkonSyncedRef.current || serverGroupSize === null) { return }
    walkonSyncedRef.current = true
    if (serverGroupSize === '1' && ttType !== 'walkon') { setTtType('walkon') }
  }, [ serverGroupSize ])

  // Puts the groups on the panel's shotgun time and holes; a shotgun is one mass start.
  const applyShotgunToGroups = async (foursomeIds, { silent = false } = {}) => setShotgun({
    leagueId,
    roundId,
    teeTime: to12hr(ttShotgunTime),
    startingHole: startHoleNumber(ttHole),
    reverseShotgun: ttHoleAssignment === 'reverse',
    foursomeIds,
    silent,
  })

  // Checks each silent write; routines stop at the first failure rather than report success.
  const wroteOk = (result, messageKey) => {
    if (!result?.error) { return true }
    showErrorNotification(result.error?.data?.error || t(`errors.${messageKey}`))
    return false
  }

  // Returns false when a write failed and the caller should stop.
  const timeGroupsInRun = async (groups, idsToTime) => {
    const pending = new Set(idsToTime)
    if (pending.size === 0) { return true }

    const interval = parseInt(ttInterval, 10) || 8
    const startingHole = startHoleNumber(ttHole)
    const startTime24 = runStartTime(ttTime)
    // A pending row's own time is ignored -- it is the one being rewritten.
    const anchors = groups.map((g) => (pending.has(g.id) ? null : to24hr(g.attributes?.tee_time)))

    const timeForRow = (index) => {
      for (let i = index - 1; i >= 0; i -= 1) {
        if (anchors[i]) { return shiftTime24(anchors[i], interval * (index - i)) }
      }
      for (let i = index + 1; i < anchors.length; i += 1) {
        if (anchors[i]) { return shiftTime24(anchors[i], -interval * (i - index)) }
      }
      return shiftTime24(startTime24, interval * index)
    }

    // In sheet order; a null time means the run crossed midnight, so that row keeps its time.
    const scheduled = groups
      .map((group, index) => ({ id: group.id, time24: timeForRow(index), pending: pending.has(group.id) }))
      .filter((row) => row.pending)
    if (scheduled.length === 0) { return true }

    // One request for an evenly spaced run; a gapped run or a refused interval goes per row.
    const evenlySpaced = interval <= MAX_RUN_INTERVAL_MINUTES && scheduled.every(({ time24 }, position) => (
      time24 && time24 === shiftTime24(scheduled[0].time24, interval * position)
    ))

    if (scheduled.length > 1 && evenlySpaced) {
      const runResult = await setTeeTimesWithInterval({
        leagueId,
        roundId,
        startTime: to12hr(scheduled[0].time24),
        intervalMinutes: interval,
        // The API and the tee-sheet payload both order rows by position, so the times line up.
        foursomeIds: scheduled.map((row) => row.id),
        silent: true,
      })
      if (!wroteOk(runResult, 'apply_tee_times_failed')) { return false }
    } else {
      for (const { id, time24 } of scheduled) {
        if (!time24) { continue }
        // eslint-disable-next-line no-await-in-loop
        const timeResult = await setOneTeeTime({
          leagueId, roundId, foursomeId: id, teeTime: to12hr(time24), silent: true,
        })
        if (!wroteOk(timeResult, 'set_tee_time_failed')) { return false }
      }
    }

    // set_hole takes the whole list, so one call for every group.
    const holeResult = await setHole({
      leagueId, roundId, hole: startingHole, foursomeIds: scheduled.map((row) => row.id), silent: true,
    })
    return wroteOk(holeResult, 'update_hole_failed')
  }

  // Joins rows to the sheet's shotgun without set_shotgun, which redistributes the field. False on failure.
  const joinShotgunOnSheet = async (groups, idsToPlace) => {
    const placed = groups.filter((group) => !idsToPlace.includes(group.id))
    const fieldTime = to12hr(to24hr(placed.map((group) => group.attributes?.tee_time).find(isPresent)))
    // In sheet order, so freeHolesInOrder carries on from the last row in the shotgun's direction.
    const free = freeHolesInOrder(placed.map((group) => group.attributes?.hole), holeCount)

    if (isPresent(fieldTime)) {
      const timeResult = await setOneTeeTime({
        leagueId, roundId, foursomeIds: idsToPlace, teeTime: fieldTime, silent: true,
      })
      if (!wroteOk(timeResult, 'apply_tee_times_failed')) { return false }
    }
    // One call per hole, none once the course runs out: two groups must never share a tee.
    for (const [ index, foursomeId ] of idsToPlace.entries()) {
      const hole = free[index]
      if (!hole) { break }
      // eslint-disable-next-line no-await-in-loop
      const holeResult = await setHole({ leagueId, roundId, hole, foursomeIds: [ foursomeId ], silent: true })
      if (!wroteOk(holeResult, 'update_hole_failed')) { return false }
    }
    return true
  }

  // Times every untimed group; `type` is passed in because setTtType has not landed yet.
  const assignTeeTimesToUntimedGroups = async (type = ttType) => {
    if (type === 'walkon') { return true }

    const { data } = await refetchTeeSheet()
    const groups = data?.data || []
    if (groups.length === 0) { return true }

    if (type === 'shotgun') {
      // Only the untimed rows: set_shotgun on every row rewrote the whole placed layout.
      const untimed = groups.filter((group) => !isPresent(group.attributes?.tee_time)).map((group) => group.id)
      if (untimed.length === 0) { return true }
      return joinShotgunOnSheet(groups, untimed)
    }

    return timeGroupsInRun(groups, groups.filter((g) => !g.attributes?.tee_time).map((g) => g.id))
  }

  // Time and hole belong to the row's position, so after a drag they are put back on it.
  const restoreSlotTimesAndHoles = async (slots) => {
    const { data } = await refetchTeeSheet()
    const groups = data?.data || []

    // Rows wanting the same value share one call; both endpoints take a list.
    const timeWrites = new Map()
    const holeWrites = new Map()

    for (const [ index, group ] of groups.entries()) {
      const slot = slots[index]
      if (!slot) { continue }
      const attrs = group.attributes || {}

      if (slot.teeTime && attrs.tee_time !== slot.teeTime) {
        entryFor(timeWrites, slot.teeTime, () => []).push(group.id)
      }

      const hole = attrs.hole || attrs.tee_number || null
      const holeChanged = String(hole || '') !== String(slot.hole || '')
        || (attrs.hole_letter || null) !== slot.holeLetter
      if (slot.hole && holeChanged) {
        // Keyed on both parts: a split start has 1A and 1B on hole 1.
        const key = `${slot.hole}|${slot.holeLetter || ''}`
        entryFor(holeWrites, key, () => ({ hole: slot.hole, holeLetter: slot.holeLetter, ids: [] })).ids.push(group.id)
      }
    }

    try {
      for (const [ teeTime, foursomeIds ] of timeWrites) {
        // eslint-disable-next-line no-await-in-loop
        const timeResult = await setOneTeeTime({ leagueId, roundId, foursomeIds, teeTime, silent: true })
        if (!wroteOk(timeResult, 'set_tee_time_failed')) { return }
      }
      for (const { hole, holeLetter, ids } of holeWrites.values()) {
        // eslint-disable-next-line no-await-in-loop
        const holeResult = await setHole({
          leagueId,
          roundId,
          hole,
          ...(holeLetter ? { holeLetter } : {}),
          foursomeIds: ids,
          silent: true,
        })
        if (!wroteOk(holeResult, 'update_hole_failed')) { return }
      }
    } finally {
      // The writes are silent, so whatever landed before a failure only shows once this runs.
      refreshTeeSheet()
    }
  }

  // Each position's tee time and hole, captured before a write that may move rows.
  const currentSlots = () => foursomes.map((group) => ({
    teeTime: group.attributes?.tee_time || null,
    hole: group.attributes?.hole || group.attributes?.tee_number || null,
    holeLetter: group.attributes?.hole_letter || null,
  }))

  const handleHoleChange = async (foursomeId, newHole, newHoleLetter) => {
    const result = await setHole({
      leagueId,
      roundId,
      hole: newHole,
      ...(newHoleLetter ? { holeLetter: newHoleLetter } : {}),
      foursomeIds: [ foursomeId ],
    })
    if (result.error) { showErrorNotification(t('errors.update_hole_failed')) }
  }

  const handleSetTeeTime = async (foursomeId, time12) => {
    const result = await setOneTeeTime({ leagueId, roundId, foursomeId, teeTime: time12 })
    if (result.error) { showErrorNotification(t('errors.set_tee_time_failed')) }
  }

  // Digits only, clamped to the course's holes; empty resolves to hole 1 on apply.
  const handleStartHoleChange = (value) => {
    const digits = value.replace(/[^0-9]/g, '')
    if (digits === '') { setTtHole(''); return }
    setTtHole(String(Math.min(Math.max(parseInt(digits, 10), 1), holeCount)))
  }

  // Read on the way into walk-on, which unplaces everyone and with them the round's tee.
  const eventTeeFromSheet = () => {
    const tee = allPlayers.find((player) => player?.tee?.id)?.tee
    if (!tee) { return {} }
    return { teeId: String(tee.id), ...(tee.course_id ? { courseId: String(tee.course_id) } : {}) }
  }

  // Every group is deleted outright; emptying them left rows holding only a blind draw.
  const applyWalkonTransition = async (prevType) => {
    if (groupSize !== '1') { preWalkonSizeRef.current = groupSize }
    setPendingGroupSize('1')

    const abandon = (messageKey) => {
      showErrorNotification(t(messageKey))
      setPendingGroupSize(null)
      setTtType(prevType)
    }

    try {
      for (const group of foursomes) {
        // eslint-disable-next-line no-await-in-loop
        const deleteRes = await deleteFoursome({ leagueId, roundId, foursomeId: group.id, silent: true })
        if (deleteRes.error) {
          abandon('errors.delete_group_failed')
          return
        }
      }
      const res = await updateRoundExpressEventSettings({ leagueId, roundId, foursome_count: 1 })
      if (res.error) { abandon('errors.group_size_failed') }
    } finally {
      // Refreshed on the abandoned paths too: a half-done transition still removed rows.
      refreshTeeSheet()
    }
  }

  // Walk-on is group size 1; leaving it restores the previous size. The start type is saved here.
  const handleTtTypeChange = async (newType) => {
    // Guarded like handleGroupSizeChange: clearing the sheet mid-job breaks the scheduler's writes.
    if (isReorganizing) { return }
    if (newType === ttType) { return }
    const prevType = ttType
    // Read before anything clears the sheet, and carried into every branch, the confirm included.
    const remembered = newType === 'walkon' ? eventTeeFromSheet() : {}
    setTtType(newType)
    dispatch(setSetupConfig({ ...(setupConfig || {}), ...remembered, startType: newType }))
    if (!roundId) { return }

    if (newType === 'walkon') {
      // Confirm first when it would empty a filled sheet, as shrinking the group size does.
      if (playerCount > 0) {
        setTtType(prevType)
        dispatch(setSetupConfig({ ...(setupConfig || {}), startType: prevType }))
        onConfirm(t('tee_times.confirm_walkon'), async () => {
          setTtType('walkon')
          dispatch(setSetupConfig({ ...(setupConfig || {}), ...remembered, startType: 'walkon' }))
          await applyWalkonTransition(prevType)
        })
        return
      }
      await applyWalkonTransition(prevType)
    } else if (prevType === 'walkon') {
      const restore = preWalkonSizeRef.current !== '1' ? preWalkonSizeRef.current : '4'
      setPendingGroupSize(restore)
      const res = await updateRoundExpressEventSettings({ leagueId, roundId, foursome_count: parseInt(restore, 10) })
      if (res.error) {
        showErrorNotification(t('errors.group_size_failed'))
        setPendingGroupSize(null)
        return
      }
      // The couple of empty groups a fresh step starts with, not the count before walk-on.
      const groupsToRestore = Math.max(0, WALKON_RESTORE_GROUPS - foursomes.length)
      for (let i = 0; i < groupsToRestore; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const createRes = await createRow({ leagueId, roundId, playerIds: [], silent: true })
        if (createRes.error) {
          showErrorNotification(t('errors.create_group_failed'))
          break
        }
      }
      // The restored groups come back untimed, so give them the panel's run.
      await assignTeeTimesToUntimedGroups(newType)
      refreshTeeSheet()
    }
  }

  // Shrinking drops every occupant of a lost seat, so ask first.
  const handleGroupSizeChange = async (size) => {
    if (isReorganizing) { return }
    if (parseInt(size, 10) < maxSize && wouldDisplaceOccupants(parseInt(size, 10))) {
      onConfirm(t('group_size.confirm_shrink'), () => applyGroupSizeChange(size))
      return
    }
    await applyGroupSizeChange(size)
  }

  const applyGroupSizeChange = async (size) => {
    setPendingGroupSize(size)
    if (!roundId) { return }

    const saveResult = await updateRoundExpressEventSettings({
      leagueId,
      roundId,
      foursome_count: parseInt(size, 10),
    })
    if (saveResult.error) {
      showErrorNotification(t('errors.group_size_failed'))
      setPendingGroupSize(null)
      return
    }

    const newSize = parseInt(size, 10)
    // Seated before the settings write, so golfers it unplaces itself can be dealt back in.
    const seatedBefore = seatedIds(foursomes)
    // Redealt into free seats, then new rows; by seat, as a group can hold a blind or a gap.
    const inRange = (occupant) => (occupant.position ?? 0) < newSize

    setIsReorganizing(true)
    try {
      // Re-read after the foursome_count write, which moves golfers itself.
      const { data } = await refetchTeeSheet()
      const groups = data?.data || []
      const overflowIds = groups
        .flatMap((g) => (g.attributes?.players || []).filter((p) => !inRange(p)))
        .map((p) => p.player_round_id || p.id)
        .filter(Boolean)
      // Whoever that write unplaced on its own, or they vanish until a reload.
      const seatedAfter = new Set(seatedIds(groups))
      const displacedIds = seatedBefore.filter((id) => !seatedAfter.has(id))

      if (overflowIds.length > 0) {
        const removeResult = await removePlayers({
          leagueId, roundId, playerIds: overflowIds, silent: true, keepEmptyFoursomes: true,
        })
        if (removeResult.error) {
          showErrorNotification(removeResult.error?.data?.error || t('errors.reorganize_groups_failed'))
          return
        }
      }

      const toRedeal = [ ...new Set([ ...displacedIds, ...overflowIds.map(String) ]) ]
      if (toRedeal.length === 0) { return }

      const remaining = [ ...toRedeal ]
      const batches = []
      for (const group of groups) {
        if (remaining.length === 0) { break }
        // Blinds included: one occupies its seat server-side.
        const taken = (group.attributes?.players || []).filter(inRange).length
          + (group.attributes?.blinds || []).filter(inRange).length
        const openSlots = Math.max(0, newSize - taken)
        if (openSlots <= 0) { continue }
        batches.push({ type: 'add', foursomeId: group.id, playerIds: remaining.splice(0, openSlots) })
      }
      let createdNewRows = false
      while (remaining.length > 0) {
        batches.push({ type: 'create', playerIds: remaining.splice(0, newSize) })
        createdNewRows = true
      }

      for (const batch of batches) {
        // eslint-disable-next-line no-await-in-loop
        const result = await (batch.type === 'add'
          ? addPlayers({ leagueId, roundId, foursomeId: batch.foursomeId, playerIds: batch.playerIds, silent: true })
          : createRow({ leagueId, roundId, playerIds: batch.playerIds, silent: true }))
        if (result.error) {
          // The API's own message when it has one.
          showErrorNotification(result.error?.data?.error || t('errors.reorganize_groups_failed'))
          return
        }
      }
      // Only the rows the redeal added need a time; the rest keep the leader's times and holes.
      if (createdNewRows) { await assignTeeTimesToUntimedGroups() }
    } finally {
      // Always, early returns included: the writes are silent.
      refreshTeeSheet()
      setIsReorganizing(false)
    }
  }

  const handleRemovePlayer = async (playerRoundId) => {
    if (!roundId || removing) { return }
    // Before the request, so the slot menu cannot be tapped again for a golfer already going.
    closeOverlay()
    // Removing one golfer must not delete their group — keep the (possibly now-empty) row.
    const result = await removePlayers({ leagueId, roundId, playerIds: [ playerRoundId ], keepEmptyFoursomes: true })
    if (result.error) { showErrorNotification(t('errors.remove_player_failed')) }
  }

  const handleDeleteGroup = (foursomeId, count) => {
    const message = count > 0
      ? t('confirm_delete_group', { count })
      : t('confirm_delete_group_empty')
    onConfirm(message, async () => {
      const result = await deleteFoursome({ leagueId, roundId, foursomeId })
      if (result.error) { showErrorNotification(t('errors.delete_group_failed')) }
    })
  }

  const handlePlaceInGroup = async (player, groupId, slotIndex) => {
    const pid = player.player_round_id || player.id
    const positions = slotIndex !== null && slotIndex !== undefined ? [ slotIndex + 1 ] : undefined
    try {
      const result = await addPlayers({
        leagueId,
        roundId,
        foursomeId: groupId,
        playerIds: [ pid ],
        ...(positions ? { positions } : {}),
        silent: true,
      })
      // The server's own reason (seat taken, group full) rather than a generic message.
      if (result.error) {
        showErrorNotification(result.error?.data?.error || t('errors.place_player_failed'))
      } else {
        await teeBenchGolfer(pid)
      }
    } finally {
      refreshTeeSheet()
    }
    closeOverlay()
  }

  const handleAddBlind = async (foursomeId, slotIndex, sourcePlayerId, removePlayerId) => {
    const position = slotIndex + 1
    if (removePlayerId) {
      const removeResult = await removePlayers({
        leagueId, roundId, playerIds: [ removePlayerId ], keepEmptyFoursomes: true,
      })
      if (removeResult.error) {
        showErrorNotification(t('errors.swap_blind_failed'))
        closeOverlay()
        return
      }
    }
    const result = await addBlindDraw({ leagueId, roundId, foursomeId, sourcePlayerId, position })
    if (result.error) {
      showErrorNotification(t('errors.add_blind_failed'))
      if (removePlayerId) {
        await addPlayers({ leagueId, roundId, foursomeId, playerIds: [ removePlayerId ], positions: [ position ] })
      }
    }
    closeOverlay()
  }

  const handleRemoveBlind = async (blindId) => {
    const result = await removeBlindDraw({ leagueId, roundId, blindId })
    if (result.error) { showErrorNotification(t('errors.remove_blind_failed')) }
    closeOverlay()
  }

  const handleSwapFromMenu = async (playerAId, playerBId) => {
    const result = await swapPlayers({ leagueId, roundId, playerAId, playerBId })
    if (result.error) {
      showErrorNotification(t('errors.swap_players_failed'))
      return
    }
    closeOverlay()
  }

  const handleAutoPlace = async (player) => {
    const pid = player.player_round_id || player.id
    // A blind draw occupies its seat server-side, so count blinds too or the add is rejected.
    const target = foursomes.find((g) =>
      ((g.attributes?.players || []).length + (g.attributes?.blinds || []).length) < maxSize)
    let result
    if (target) {
      result = await addPlayers({ leagueId, roundId, foursomeId: target.id, playerIds: [ pid ], silent: true })
      if (result.error) { showErrorNotification(result.error?.data?.error || t('errors.place_player_failed')) }
    } else {
      result = await createRow({ leagueId, roundId, playerIds: [ pid ], silent: true })
      if (result.error) { showErrorNotification(t('errors.create_group_failed')) }
    }
    try {
      if (!result.error) {
        await teeBenchGolfer(pid)
        await assignTeeTimesToUntimedGroups()
      }
    } finally {
      refreshTeeSheet()
    }
  }

  // The new row takes the panel's next time and hole; its id comes from a refetch.
  const handleAddGroup = async () => {
    const knownIds = new Set(foursomes.map((f) => f.id))
    const result = await createRow({ leagueId, roundId, playerIds: [], silent: true })
    if (result.error) {
      showErrorNotification(t('errors.create_group_failed'))
      return
    }
    try {
      if (ttType === 'walkon') { return }

      const { data } = await refetchTeeSheet()
      const groups = data?.data || []
      const newIds = groups.map((g) => g.id).filter((id) => !knownIds.has(id))
      if (newIds.length === 0) { return }

      if (ttType === 'shotgun') {
        await applyShotgunToGroups(groups.map((g) => g.id), { silent: true })
        return
      }

      await timeGroupsInRun(groups, newIds)
    } finally {
      // Covers the early returns too: the row was created silently.
      refreshTeeSheet()
    }
  }

  const rowIndexOf = (group) => foursomes.findIndex((one) => String(one.id) === String(group.id))

  // Arrow keys reorder a row: dragging was the only way, which no keyboard user has.
  const handleGripKey = (rowIndex) => (event) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') { return }
    event.preventDefault()
    handleRowDrop(rowIndex, rowIndex + (event.key === 'ArrowUp' ? -1 : 1))
  }

  const handleRowDrop = async (fromIndex, toIndex) => {
    if (fromIndex === null || fromIndex === toIndex) { return }
    if (toIndex < 0 || toIndex >= foursomes.length) { return }
    const reordered = [ ...foursomes ]
    const [ moved ] = reordered.splice(fromIndex, 1)
    reordered.splice(toIndex, 0, moved)

    // Golfers only: re-applying the panel's run threw away per-group times and holes.
    const slots = currentSlots()
    // silent: restoreSlotTimesAndHoles refetches and refreshes the sheet itself.
    const result = await reorganize({
      leagueId, roundId, foursomeIds: reordered.map((g) => g.id), silent: true,
    })
    if (result.error) {
      showErrorNotification(t('errors.reorder_groups_failed'))
      return
    }
    await restoreSlotTimesAndHoles(slots)
  }

  const handleBlindToEmptySlotDrop = async (source, targetFoursomeId, slotIdx) => {
    const { blindId, rosterMemberId } = source.blindData
    const srcPlayer = allPlayers.find((p) => p.player_roster_id === rosterMemberId)
    if (!srcPlayer) {
      showErrorNotification(t('errors.move_blind_failed'))
      return
    }
    const removeResult = await removeBlindDraw({ leagueId, roundId, blindId })
    if (removeResult.error) {
      showErrorNotification(t('errors.move_blind_failed'))
      return
    }
    const addResult = await addBlindDraw({
      leagueId,
      roundId,
      foursomeId: targetFoursomeId,
      sourcePlayerId: srcPlayer.player_round_id,
      position: slotIdx + 1,
    })
    if (addResult.error) { showErrorNotification(t('errors.move_blind_failed')) }
  }

  // Each step says how to undo itself; writes are silent and the sheet refetches once at the end.
  const runSwap = async (steps, messageKey) => {
    const undos = []
    try {
      for (const { apply, undo } of steps) {
        // eslint-disable-next-line no-await-in-loop
        const result = await apply()
        if (!result?.error) {
          if (undo) { undos.unshift(undo) }
          continue
        }
        for (const rollback of undos) {
          // eslint-disable-next-line no-await-in-loop
          await rollback()
        }
        showErrorNotification(t(`errors.${messageKey}`))
        return false
      }
      return true
    } finally {
      refreshTeeSheet()
    }
  }

  // Silent seat writes for runSwap, which refreshes the sheet once the swap ends.
  const seat = (foursomeId, playerId, position) => addPlayers({
    leagueId,
    roundId,
    foursomeId,
    playerIds: [ playerId ],
    ...(position === undefined ? {} : { positions: [ position ] }),
    silent: true,
  })
  const unseat = (playerId) => removePlayers({
    leagueId, roundId, playerIds: [ playerId ], keepEmptyFoursomes: true, silent: true,
  })
  const blindAt = (foursomeId, sourcePlayerId, position) => addBlindDraw({
    leagueId, roundId, foursomeId, sourcePlayerId, position, silent: true,
  })
  const unblind = (blindId) => removeBlindDraw({ leagueId, roundId, blindId, silent: true })

  // A bench golfer may hold no tee and show the row's; Individual is skipped, as removal keeps a picked tee.
  // Warns rather than fails: the golfer is seated either way ({} for runSwap).
  const teeBenchGolfer = async (playerRoundId) => {
    const golfer = unplaced.find((p) => String(p.player_round_id) === String(playerRoundId))
    const sheet = sheetGenderTees(allPlayers)
    if (!roundId || !golfer || !sheet) { return {} }
    const women = playsWomensTee(golfer)
    const sheetTee = women ? sheet.women : sheet.men
    const configTee = women ? setupConfig?.walkonTeeF : setupConfig?.walkonTeeM
    // First pass: Course & Tee has not run, so there is nothing to copy yet.
    if (!sheetTee && !configTee) { return {} }
    // A parent tee from the setup needs the event's holes; a sheet tee already is the right nine.
    const nine = !sheetTee && [ '2', '3' ].includes(setupConfig?.walkonHoleType)
    const result = await setCourseAndTee({
      leagueId,
      roundId,
      teeId: String(sheetTee?.id || configTee),
      playerIds: [ String(playerRoundId) ],
      ...(nine ? { holesType: holesTypeFor(setupConfig.walkonHoleType) } : {}),
      silent: true,
    })
    if (result.error) { showErrorNotification(t('errors.tee_assignment_failed')) }
    return {}
  }
  const benchTeeStep = (source) => (source.foursomeId === null
    ? [ { apply: () => teeBenchGolfer(source.playerRoundId) } ]
    : [])

  // Takes a dragged golfer out of their group, and back into the same seat if a later step fails.
  const unseatStep = (source) => (source.foursomeId === null ? [] : [ {
    apply: () => unseat(source.playerRoundId),
    undo: () => seat(source.foursomeId, source.playerRoundId, source.position ? source.position + 1 : undefined),
  } ])

  const moveTo = (source, foursomeId, position) => runSwap([
    ...unseatStep(source),
    { apply: () => seat(foursomeId, source.playerRoundId, position) },
    ...benchTeeStep(source),
  ], 'move_player_failed')

  const handlePlayerToBlindDrop = async (source, targetFoursomeId, targetBlind) => {
    const targetBlindPlayer = allPlayers.find((p) => p.player_roster_id === targetBlind.player_roster_id)

    await runSwap([
      {
        apply: () => unblind(targetBlind.blind_id),
        undo: targetBlindPlayer
          && (() => blindAt(targetFoursomeId, targetBlindPlayer.player_round_id, targetBlind.position + 1)),
      },
      ...unseatStep(source),
      { apply: () => seat(targetFoursomeId, source.playerRoundId, targetBlind.position + 1) },
      ...benchTeeStep(source),
    ], 'move_player_failed')
  }

  // add_blinds refuses a taken seat, so the old blind goes first and comes back if the new one fails.
  const handleReplaceBlind = async (foursomeId, blind, sourcePlayerId) => {
    const oldSource = [ ...allPlayers, ...unplaced ].find((p) => p.player_roster_id === blind.player_roster_id)

    await runSwap([
      {
        apply: () => unblind(blind.blind_id),
        undo: oldSource && (() => blindAt(foursomeId, oldSource.player_round_id, blind.position + 1)),
      },
      { apply: () => blindAt(foursomeId, sourcePlayerId, blind.position + 1) },
    ], 'add_blind_failed')
    closeOverlay()
  }

  const handleBlindToPlayerDrop = async (source, targetFoursomeId, targetPlayer) => {
    const { blindId, rosterMemberId, position: blindPosition } = source.blindData
    const srcBlindPlayer = allPlayers.find((p) => p.player_roster_id === rosterMemberId)
    if (!srcBlindPlayer) {
      showErrorNotification(t('errors.swap_failed'))
      return
    }
    const targetPlayerId = targetPlayer.player_round_id
    const targetPosition = targetPlayer.position

    await runSwap([
      {
        apply: () => unseat(targetPlayerId),
        undo: () => seat(targetFoursomeId, targetPlayerId, targetPosition + 1),
      },
      {
        apply: () => unblind(blindId),
        undo: () => blindAt(source.foursomeId, srcBlindPlayer.player_round_id, blindPosition + 1),
      },
      { apply: () => seat(source.foursomeId, targetPlayerId, blindPosition + 1) },
      { apply: () => blindAt(targetFoursomeId, srcBlindPlayer.player_round_id, targetPosition + 1) },
    ], 'swap_failed')
  }

  const handleBlindToBlindDrop = async (source, targetFoursomeId, targetBlind) => {
    const { blindId: srcBlindId, rosterMemberId: srcRosterId, position: srcPosition } = source.blindData
    const srcPlayer = allPlayers.find((p) => p.player_roster_id === srcRosterId)
    const tgtPlayer = allPlayers.find((p) => p.player_roster_id === targetBlind.player_roster_id)
    if (!srcPlayer || !tgtPlayer) {
      showErrorNotification(t('errors.swap_failed'))
      return
    }
    const tgtPosition = targetBlind.position

    await runSwap([
      {
        apply: () => unblind(srcBlindId),
        undo: () => blindAt(source.foursomeId, srcPlayer.player_round_id, srcPosition + 1),
      },
      {
        apply: () => unblind(targetBlind.blind_id),
        undo: () => blindAt(targetFoursomeId, tgtPlayer.player_round_id, tgtPosition + 1),
      },
      { apply: () => blindAt(targetFoursomeId, srcPlayer.player_round_id, tgtPosition + 1) },
      { apply: () => blindAt(source.foursomeId, tgtPlayer.player_round_id, srcPosition + 1) },
    ], 'swap_failed')
  }

  const handlePlayerDrop = async (targetFoursomeId, targetPlayer) => {
    const source = dnd.playerDragSource
    dnd.clearPlayerDrag()
    if (!source) { return }

    const sourceId = source.playerRoundId
    const targetId = targetPlayer?.player_round_id || null
    if (sourceId === targetId) { return }

    const targetIsBlind = targetPlayer?._type === 'blind'
    if (source.blindData && targetIsBlind) { return handleBlindToBlindDrop(source, targetFoursomeId, targetPlayer) }
    if (source.blindData && targetPlayer) { return handleBlindToPlayerDrop(source, targetFoursomeId, targetPlayer) }
    if (source.blindData) { return undefined }
    if (targetIsBlind) { return handlePlayerToBlindDrop(source, targetFoursomeId, targetPlayer) }
    if (!targetId && source.foursomeId === targetFoursomeId) { return undefined }

    if (!targetId) {
      await moveTo(source, targetFoursomeId)
      return undefined
    }

    await runSwap([
      { apply: () => swapPlayers({ leagueId, roundId, playerAId: sourceId, playerBId: targetId, silent: true }) },
      ...benchTeeStep(source),
    ], 'swap_players_failed')
    return undefined
  }

  const handleEmptySlotDrop = async (targetFoursomeId, slotIdx) => {
    const source = dnd.playerDragSource
    dnd.clearPlayerDrag()
    if (!source) { return }
    if (source.blindData) { return handleBlindToEmptySlotDrop(source, targetFoursomeId, slotIdx) }

    await moveTo(source, targetFoursomeId, slotIdx + 1)
    return undefined
  }

  const showPlaceAll = isReorganizing ? placingField : playerCount === 0

  const handleSubmitReorganize = async ({ algorithm, optimization, metadatumId, consolidate, entryNumberFieldId }) => {
    if (!roundId) { return }
    setReorganizeModalOpen(false)
    setPlacingField(playerCount === 0)
    setIsReorganizing(true)
    // Captured before the job, so the rows it adds can be timed afterwards.
    const knownIds = new Set(foursomes.map((group) => group.id))
    const effectiveMetadatumId = algorithm === 'entry_number' ? entryNumberFieldId : metadatumId
    const result = await reorganizePlayers({
      leagueId,
      roundId,
      schedulingOption: algorithm,
      ...(algorithm === 'ab' || algorithm === 'abcd' ? { optimization } : {}),
      ...(isPresent(effectiveMetadatumId) ? { metadatumId: effectiveMetadatumId } : {}),
      ...(algorithm === 'custom_field' ? { consolidateCustomFieldFoursomes: consolidate } : {}),
    })
    if (result.error || !result.data?.job_id) {
      setIsReorganizing(false)
      showErrorNotification(result.error?.data?.errors?.[0] || t('errors.reorganize_action_failed'))
      return
    }

    const { outcome } = await pollJob(result.data.job_id)
    if (outcome === REORGANIZE_OUTCOME.CANCELLED) { return }
    // Anything that is not COMPLETED is a failure.
    if (outcome !== REORGANIZE_OUTCOME.COMPLETED) {
      setIsReorganizing(false)
      notifyFailure(t('errors.reorganize_action_failed'))
      return
    }
    // finally, so a throw cannot leave the button reading "Reorganizing...".
    try {
      // Reorganize seats the whole roster, so the bench is refetched too.
      const [ sheet ] = await Promise.all([ refetchTeeSheet(), refetchUnplaced() ])
      await timeRowsAddedByReorganize(knownIds, sheet?.data?.data || [])
    } finally {
      setIsReorganizing(false)
    }
  }

  // Rows the scheduler adds carry the Express Event's interval and hole; keyed on new ids.
  const timeRowsAddedByReorganize = async (knownIds, groups) => {
    if (ttType === 'walkon' || groups.length === 0) { return }

    const newIds = groups.map((group) => group.id).filter((id) => !knownIds.has(id))
    if (newIds.length === 0) { return }

    // In a finally, like every silent sequence here.
    try {
      if (ttType === 'shotgun') {
        // New rows only: set_shotgun rewrites every placed row and needs a tee reorganize stripped.
        await joinShotgunOnSheet(groups, newIds)
      } else {
        // Anchors ignore a pending row's own time; the starting hole is corrected in the same pass.
        await timeGroupsInRun(groups, newIds)
      }
    } finally {
      refreshTeeSheet()
    }
  }

  // `untimedOnly` (Continue) fills untimed groups only; Apply rewrites every group. False on failure.
  const handleApplyTeeTimes = async ({ untimedOnly = false } = {}) => {
    if (!roundId || ttType === 'walkon' || foursomes.length === 0) { return true }

    const reverseDir = ttHoleAssignment === 'reverse'
    dispatch(setSetupConfig({
      ...(setupConfig || {}),
      startType: ttType,
      teeTime: ttTime,
      interval: ttInterval,
      startHole: ttHole,
      shotgunTime: ttShotgunTime,
      reverseDir,
    }))

    if (untimedOnly) {
      // The untimed pass writes silently, so the sheet is read back here.
      const timed = await assignTeeTimesToUntimedGroups()
      refreshTeeSheet()
      return timed
    }

    const allFoursomeIds = foursomes.map((f) => f.id)
    const results = []
    if (ttType === 'teetimes') {
      results.push(await setTeeTimesWithInterval({
        leagueId,
        roundId,
        startTime: to12hr(ttTime),
        intervalMinutes: parseInt(ttInterval, 10),
        foursomeIds: allFoursomeIds,
      }))
      results.push(await setHole({ leagueId, roundId, hole: startHoleNumber(ttHole), foursomeIds: allFoursomeIds }))
    } else if (ttType === 'shotgun') {
      // A shotgun needs a tee on every group (422 "Cannot infer tee."), so assign them first.
      if (!(await assignTeesToPlacedGolfers())) { return false }
      results.push(await applyShotgunToGroups(allFoursomeIds))
    }

    // Every write can fail; a 422 must not collapse the panel as though it worked.
    const failure = results.find((result) => result?.error)
    if (failure) {
      notifyFailure(failure.error?.data?.error || t('errors.apply_tee_times_failed'))
      return false
    }

    // Collapse to the summary, only on success.
    configUserSetRef.current = true
    setConfigOpen(false)
    return true
  }

  // Re-affirms each placed golfer's own tee, grouped by tee; late golfers and shotguns need one.
  const assignTeesToPlacedGolfers = async () => {
    if (!roundId || !allPlayers.length) { return true }

    const courseId = fieldCourseId(allPlayers, setupConfig?.courseId)
    const fallbackTeeId = fieldTeeId(allPlayers, { courseId, configTeeId: setupConfig?.teeId })
    const fallbackTees = {
      men: setupConfig?.walkonTeeM || fallbackTeeId,
      women: setupConfig?.walkonTeeF || fallbackTeeId,
    }

    for (const [ teeId, playerIds ] of teeWritesForField(allPlayers, { courseId, fallbackTeeId, fallbackTees })) {
      // eslint-disable-next-line no-await-in-loop
      const result = await setCourseAndTee({ leagueId, roundId, teeId, playerIds })
      if (result.error) { showErrorNotification(t('errors.tee_assignment_failed')); return false }
    }
    return true
  }

  const handleContinue = async () => {
    const proceed = async () => {
      // Stop on either failure, or tee-less golfers reach score entry as "Tee Missing".
      if (foursomes.length > 0 && ttType !== 'walkon') {
        if (!(await handleApplyTeeTimes({ untimedOnly: true }))) { return }
      }
      if (ttType !== 'walkon' && !(await assignTeesToPlacedGolfers())) { return }
      dispatch(markStepCompleted('pairings'))
      dispatch(goToNextStep())
    }
    // Walk-on has no tee sheet, so unplaced golfers are expected and skip the confirm.
    if (!isWalkon && unplaced.length > 0) {
      onConfirm(t('confirm_unplaced', { count: unplaced.length }), proceed)
      return
    }
    await proceed()
  }

  if (sheetLoading && foursomes.length === 0) { return <LoadingState label={t('sheet.loading')}/> }

  const openSlotMenu = (event, foursomeId, slotIndex, player) => {
    event.stopPropagation()
    const isSame = overlay?.kind === 'slotMenu'
      && overlay.foursomeId === foursomeId
      && overlay.slotIndex === slotIndex
    setOverlay(isSame ? null : {
      kind: 'slotMenu',
      foursomeId,
      slotIndex,
      player,
      rect: event.currentTarget.getBoundingClientRect(),
    })
  }

  // An empty seat opens the golfer list directly; "Add blind draw" is in its footer.
  const openEmptySlot = (event, foursomeId, slotIndex) => {
    event.stopPropagation()
    setOverlay({
      kind: 'slotPicker',
      foursomeId,
      slotIndex,
      rect: event.currentTarget.getBoundingClientRect(),
    })
  }

  // Hover handlers shared by every seat, empty or not.
  const seatHoverHandlers = (group, slotIndex) => ({
    onDragOver: (event) => {
      if (!dnd.isDraggingPlayer()) { return }
      event.preventDefault()
      event.stopPropagation()
      event.dataTransfer.dropEffect = 'move'
      dnd.markPlayerDragOver(group.id, slotIndex)
    },
    onDragLeave: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) { dnd.setPlayerDragOverTarget(null) }
    },
  })

  // Drag handlers shared by an occupied seat, whether it holds a golfer or a blind.
  const seatDragHandlers = (group, player, slotIndex, blindData) => ({
    draggable: true,
    onDragStart: (event) => {
      dnd.startPlayerDrag(event, {
        source: {
          foursomeId: group.id,
          playerRoundId: player.player_round_id,
          // So a failed swap can put the golfer back in the same seat.
          position: player.position,
          ...(blindData ? { blindData } : {}),
        },
        name: blindData ? `[${playerFullName(player)}]` : playerFullName(player),
        initials: playerInitials(player),
        handicapIndex: player.handicap_index,
      })
      closeOverlay()
    },
    ...seatHoverHandlers(group, slotIndex),
    onDrop: (event) => {
      event.preventDefault()
      event.stopPropagation()
      handlePlayerDrop(group.id, player)
    },
    onDragEnd: () => dnd.clearPlayerDrag(),
  })

  const renderSeat = (group, player, slotIndex) => {
    // Seats past the configured size only reveal an occupant; their empty cells offer nothing.
    if (!player && slotIndex >= maxSize) {
      return <div className="gl-event-setup-slot-picker-wrap" aria-hidden="true"/>
    }
    if (!player) {
      const isDragTarget = dnd.isPlayerDragOver(group.id, slotIndex)
      return (
        <div className="gl-event-setup-slot-picker-wrap">
          <button
            type="button"
            className={`gl-event-setup-slot-add-btn${isDragTarget ? ' is-drag-over' : ''}`}
            aria-label={t('sheet.slot_options_aria')}
            {...seatHoverHandlers(group, slotIndex)}
            onDrop={(event) => {
              if (!dnd.isDraggingPlayer()) { return }
              event.preventDefault()
              event.stopPropagation()
              handleEmptySlotDrop(group.id, slotIndex)
            }}
            onClick={(event) => openEmptySlot(event, group.id, slotIndex)}
          >
            {unplaced.length > 0 ? '+' : '-'}
          </button>
        </div>
      )
    }

    const isBlind = player._type === 'blind'
    const fullName = playerFullName(player)
    const beingDragged = dnd.isBeingDragged(player.player_round_id)
    const dragTarget = !beingDragged && dnd.isPlayerDragOver(group.id, slotIndex)
    const blindData = isBlind
      ? { blindId: player.blind_id, rosterMemberId: player.player_roster_id, position: player.position }
      : null
    const stateClasses = `${beingDragged ? ' is-dragging' : ''}${dragTarget ? ' is-drag-over' : ''}`
    // A blind's seat is itself the control; a golfer's seat holds the remove button, so the grip does.
    const Seat = isBlind ? 'button' : 'div'

    return (
      <Seat
        type={isBlind ? 'button' : undefined}
        className={`${isBlind ? 'gl-event-setup-blind-slot' : 'gl-event-setup-sh-slot'}${stateClasses}`}
        aria-label={isBlind ? t('sheet.blind_aria', { name: fullName }) : undefined}
        onClick={(event) => openSlotMenu(event, group.id, slotIndex, player)}
        {...seatDragHandlers(group, player, slotIndex, blindData)}
        >
        {isBlind ? (
          <>
            <div className="gl-event-setup-blind-av" aria-hidden="true">{playerInitials(player)}</div>
            <div className="gl-player-info">
              {/* a blind draw is shown bracketed, in its own class, so PlayerIdentity does not fit */}
              <div className="gl-event-setup-blind-nm">[{fullName}]</div>
              {formatHandicap(player.handicap_index) !== null && (
                <div className="gl-player-hi">
                  {sharedT('hi_display', { value: formatHandicap(player.handicap_index) })}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <button
              className="gl-event-setup-p-grip"
              type="button"
              aria-label={t('sheet.slot_options_aria')}
              onClick={(event) => { event.stopPropagation(); openSlotMenu(event, group.id, slotIndex, player) }}
            >
              ⠿
            </button>
            <button
              className="gl-event-setup-p-rm"
              type="button"
              aria-label={t('sheet.remove_player_aria', { name: fullName })}
              onClick={(event) => { event.stopPropagation(); handleRemovePlayer(player.player_round_id) }}
              disabled={removing}
            >
              &times;
            </button>
            <PlayerRow player={player} name={fullName}/>
          </>
        )}
      </Seat>
    )
  }

  const sheetColumns = [
    { key: 'grip', label: '', className: 'gl-event-setup-sh-col-grip' },
    { key: 'hole', label: t('sheet.col_hole'), className: 'gl-event-setup-sh-col-hole' },
    { key: 'time', label: t('sheet.col_tee_time'), className: 'gl-event-setup-sh-col-time' },
    ...Array.from({ length: slotCount }, (_, i) => ({
      key: `slot-${i}`,
      label: t('sheet.col_golfer', { n: i + 1 }),
      slotIndex: i,
      className: 'gl-event-setup-sh-col-slot',
    })),
    { key: 'actions', label: '', className: 'gl-event-setup-sh-col-del' },
  ]

  const renderSheetCell = (group, column) => {
    const attrs = group.attributes || {}
    switch (column.key) {
      case 'grip':
        return (
          <button
            type="button"
            className="gl-event-setup-sh-drag"
            title={t('sheet.drag_title')}
            aria-label={t('sheet.move_group_aria', { n: rowIndexOf(group) + 1 })}
            onKeyDown={handleGripKey(rowIndexOf(group))}
            >
            ⠿
          </button>
        )
      case 'hole':
        return (
          <HoleCell
            hole={attrs.hole || attrs.tee_number || 1}
            holeLetter={attrs.hole_letter}
            foursomeId={group.id}
            onSave={handleHoleChange}
            holeCount={holeCount}
          />
        )
      case 'time':
        return <TeeTimeCell teeTime={attrs.tee_time || null} foursomeId={group.id} onSave={handleSetTeeTime}/>
      case 'actions':
        return (
          <button
            className="gl-event-setup-sh-row-del"
            type="button"
            aria-label={t('sheet.delete_group_aria')}
            onClick={() => handleDeleteGroup(group.id, (attrs.players || []).length)}
            disabled={deletingRow}
            >
            &times;
          </button>
        )
      default:
        return renderSeat(group, buildSlots(attrs.players || [], attrs.blinds || [], slotCount)[column.slotIndex], column.slotIndex)
    }
  }

  // Rows reorder by drag; the check keeps a golfer drag from reordering its row.
  const sheetRowProps = (group, index) => ({
    draggable: true,
    className: `gl-event-setup-sh-row${dnd.rowDragIndex === index ? ' is-dragging' : ''}${dnd.rowDragOverIndex === index ? ' is-drag-over' : ''}`,
    onDragStart: (event) => {
      if (dnd.isDraggingPlayer()) { return }
      dnd.setRowDragIndex(index)
      event.dataTransfer.effectAllowed = 'move'
    },
    onDragOver: (event) => {
      if (dnd.isDraggingPlayer()) {
        if ((group.attributes?.players || []).length >= maxSize) { return }
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
        dnd.markPlayerDragOver(group.id, null)
        return
      }
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      if (dnd.rowDragOverIndex !== index) { dnd.setRowDragOverIndex(index) }
    },
    onDragLeave: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        if (dnd.isDraggingPlayer()) {
          dnd.setPlayerDragOverTarget(null)
        } else {
          dnd.setRowDragOverIndex(null)
        }
      }
    },
    onDrop: async (event) => {
      if (dnd.isDraggingPlayer()) {
        if ((group.attributes?.players || []).length >= maxSize) { return }
        event.preventDefault()
        await handlePlayerDrop(group.id, null)
        return
      }
      event.preventDefault()
      const from = dnd.rowDragIndex
      dnd.clearRowDrag()
      await handleRowDrop(from, index)
    },
    onDragEnd: () => {
      if (dnd.isDraggingPlayer()) { return }
      dnd.clearRowDrag()
    },
  })

  return (
    <>
      <LiveMessage message={liveMessage}/>
      <div className="gl-event-setup-wrap">
        <PageHeader title={t('title')} subtitle={t('subtitle')}/>

        <PairingsConfigPanel
          open={configOpen}
          onToggle={toggleConfig}
          summary={configSummary}
          groupSize={groupSize}
          onGroupSizeChange={handleGroupSizeChange}
          isWalkon={isWalkon}
          teamGamePresent={teamGamePresent}
          locked={isReorganizing}
          courseName={courseName}
          holeCount={holeCount}
          canApply={foursomes.length > 0}
          schedule={{
            type: ttType,
            onTypeChange: handleTtTypeChange,
            teeTime: ttTime,
            setTeeTime: setTtTime,
            interval: ttInterval,
            setInterval: setTtInterval,
            startHole: ttHole,
            onStartHoleChange: handleStartHoleChange,
            shotgunTime: ttShotgunTime,
            setShotgunTime: setTtShotgunTime,
            holeAssignment: ttHoleAssignment,
            setHoleAssignment: setTtHoleAssignment,
            onApply: handleApplyTeeTimes,
            applying: applyingTimes,
          }}
        />

        {isWalkon ? (
          <div className="gl-event-setup-sheet-wrap">
            <div className="gl-event-setup-sheet-msg" role="note">{t('walkon.note')}</div>
            {walkonPlayers.length > 0 ? (
              <ul className="gl-event-setup-walkon-roster" aria-label={t('walkon.roster_aria')}>
                {walkonPlayers.map((player) => (
                  <li key={player.player_round_id || player.id} className="gl-event-setup-walkon-row">
                    <PlayerRow player={player}/>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="gl-event-setup-sheet-msg">{t('walkon.empty')}</div>
            )}
          </div>
        ) : (
        <>
        <div className="gl-event-setup-sheet-wrap">
          <div className="gl-event-setup-sh-toolbar-row">
            <div className="gl-event-setup-sh-status">
              <span className="gl-event-setup-sh-status-dot" aria-hidden="true"/>
              <span className="gl-event-setup-sh-status-text">
                {sheetLoading ? '…' : (
                  <>
                    {t('sheet.status_on_sheet', { count: playerCount })}
                    {unplaced.length > 0 && t('sheet.status_not_on_sheet', { count: unplaced.length })}
                    {t('sheet.status_groups', { count: foursomes.length })}
                  </>
                )}
              </span>
            </div>
            {/* Only reached for tee times and shotgun -- walk-on renders no sheet. */}
            {teamGamePresent && (
              <p className="gl-event-setup-sh-teams-note">{t('tee_times.teams_note')}</p>
            )}
            <div className="gl-event-setup-sh-toolbar-actions">
              {/* Reorganize only needs golfers to place; it creates the rows it needs. */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setReorganizeModalOpen(true)}
                disabled={isReorganizing || playerCount + unplaced.length === 0}
              >
                {showPlaceAll ? t('sheet.place_all') : t('sheet.reorganize')}
              </Button>
              <Button variant="secondary" size="sm" onClick={handleAddGroup} disabled={creatingRow}>
                {t('sheet.add_group')}
              </Button>
            </div>
          </div>

          {(sheetLoading || isReorganizing) && (
            <div className="gl-event-setup-sheet-msg" role="status" aria-live="polite">
              {isReorganizing
                ? t(placingField ? 'sheet.placing' : 'sheet.reorganizing')
                : t('sheet.loading')}
            </div>
          )}

          {!sheetLoading && !isReorganizing && foursomes.length === 0 && (
            <div className="gl-event-setup-sheet-msg">{t('sheet.empty')}</div>
          )}

          {!isReorganizing && foursomes.length > 0 && (
            <DataTable
              columns={sheetColumns}
              rows={foursomes}
              rowKey={(group) => group.id}
              renderCell={renderSheetCell}
              rowProps={sheetRowProps}
              ariaLabel={t('sheet.aria_label')}
              renderMobileRow={(group) => (
                <PairingsMobileCard
                  group={group}
                  slotCount={slotCount}
                  maxSize={maxSize}
                  hasBenchGolfers={unplaced.length > 0}
                  onDelete={handleDeleteGroup}
                  deleting={deletingRow}
                  onOpenEmptySlot={openEmptySlot}
                  onOpenSlotMenu={openSlotMenu}
                />
              )}
            />
          )}
        </div>

        {unplaced.length > 0 && !isReorganizing && (
          <PairingsBench
            players={unplaced}
            onPlace={handleAutoPlace}
            busy={addingPlayer || creatingRow}
            dnd={dnd}
          />
        )}
        </>
        )}

        <WizardFooter
          onBack={() => dispatch(goToPreviousStep())}
          // Guarded like the type and size changes: advancing mid-job takes a half-built sheet along.
          onPrimary={handleContinue}
          primaryDisabled={isReorganizing}
          primaryLabel={t('continue')}
          hint={isWalkon
            // Walk-on has no tee sheet, so show a neutral roster count instead of the warning.
            ? t('hint_walkon', { count: walkonPlayers.length })
            : playerCount === 0 && unplaced.length === 0 ? t('hint_empty') : (
              <>
                {t('sheet.status_on_sheet', { count: playerCount })}
                {unplaced.length > 0 && (
                  <span className="gl-event-setup-bot-hint-warn">
                    {t('hint_not_on_sheet', { count: unplaced.length })}
                  </span>
                )}
              </>
            )}
        />
      </div>

      <PairingsOverlays
        overlay={overlay}
        setOverlay={setOverlay}
        onClose={closeOverlay}
        unplaced={unplaced}
        allPlayers={allPlayers}
        addingPlayer={addingPlayer}
        actions={{
          placeInGroup: handlePlaceInGroup,
          addBlind: handleAddBlind,
          replaceBlind: handleReplaceBlind,
          removeBlind: handleRemoveBlind,
          removePlayer: handleRemovePlayer,
          swap: handleSwapFromMenu,
        }}
      />

      {reorganizeModalOpen && (
        <ReorganizeModal
          placing={playerCount === 0}
          onClose={() => setReorganizeModalOpen(false)}
          onSubmit={handleSubmitReorganize}
          groupSize={maxSize}
          customFields={customFields}
          submitting={isReorganizing}
        />
      )}
    </>
  )
}

export default PairingsStep
